// Teams and players data to be shared across components
// This serves as the master data source for teams and player information

export const teamsByDivision = {
  'Division 1': [
    'Newport I', 'Monmouthshire I', 'St. Pierre I', 'Dewstow I', 
    'Llanwern I', 'Parc I', 'St. Mellons I', 'Celtic Manor I'
  ],
  'Division 2': [
    'Alice Springs I', 'St. Mellons II', 'Pontnewydd I', 'Raglan Parc I'
  ],
  'Division 3': [
    'Celtic Manor II', 'Woodlake Park I'
  ],
  'Division 4': [
    'Parc III', 'Pontnewydd I', 'West Mon III', 'Newport II',
    'St Mellons II', 'Monmouthshire II', 'Blackwood I', 'St. Pierre IV', 
    'Woodlake Park II', 'Wernddu I'
  ],
  'Division 5': ['Team 5I', 'Team 5II', 'Team 5III'],
  'Division 6': ['Team 6I', 'Team 6II'],
  'Division 7': ['Team 7I', 'Team 7II', 'Team 7III', 'Team 7IV']
};

// Initialize the players data with 5 nominated players for each team
export const playersDataByTeam: {
  [key: string]: Array<{
    id: number;
    name: string;
    whsId: string;
    nominated: boolean;
    matchesPlayed: number;
    matchesWon: number;
    matchesDrawn: number;
    matchesLost: number;
  }>;
} = {};

// Random WHS ID generator
const generateWhsId = (): string => {
  let whsId = '';
  for (let i = 0; i < 10; i++) {
    whsId += Math.floor(Math.random() * 10).toString();
  }
  return whsId;
};

// Deterministic player name generator using the team name as a seed
const generateDeterministicPlayerNames = (teamName: string, count: number): string[] => {
  const firstNames = ["John", "David", "Michael", "Robert", "James", "William", "Richard", "Thomas", 
                     "Charles", "Daniel", "Matthew", "Anthony", "Mark", "Paul", "Steven", "Andrew",
                     "Kenneth", "George", "Edward", "Brian", "Ronald", "Kevin", "Gary", "Nicholas"];
  
  const lastNames = ["Smith", "Johnson", "Williams", "Jones", "Brown", "Davis", "Miller", "Wilson",
                    "Moore", "Taylor", "Anderson", "Thomas", "Jackson", "White", "Harris", "Martin",
                    "Thompson", "Garcia", "Martinez", "Robinson", "Clark", "Rodriguez", "Lewis", "Lee"];
  
  const getHash = (str: string): number => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash = hash & hash;
    }
    return Math.abs(hash);
  };
  
  const teamHash = getHash(teamName);
  const names: string[] = [];
  
  for (let i = 0; i < count; i++) {
    const firstNameIndex = (teamHash + i * 7) % firstNames.length;
    const lastNameIndex = (teamHash + i * 13) % lastNames.length;
    
    const firstName = firstNames[firstNameIndex];
    const lastName = lastNames[lastNameIndex];
    names.push(`${firstName} ${lastName}`);
  }
  
  return names;
};

// Add players to each team
let nextPlayerId = 1;
Object.keys(teamsByDivision).forEach(division => {
  const teams = teamsByDivision[division as keyof typeof teamsByDivision];
  teams.forEach(team => {
    const playerNames = generateDeterministicPlayerNames(team, 8);
    const players = playerNames.map((name, index) => ({
      id: nextPlayerId++,
      name,
      whsId: generateWhsId(),
      nominated: index < 5,
      matchesPlayed: 0,
      matchesWon: 0,
      matchesDrawn: 0,
      matchesLost: 0
    }));
    playersDataByTeam[team] = players;
  });
});

// League standings structure
export interface TeamStanding {
  position: number;
  team: string;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  points: number;
  gamePoints: number;
  totalPoints: number;
  lastUpdated?: string;
}

// Reset league standings with default values
export const leagueStandings: { [division: string]: TeamStanding[] } = {};

// Initialize standings for each division
Object.keys(teamsByDivision).forEach(division => {
  leagueStandings[division] = teamsByDivision[division as keyof typeof teamsByDivision].map((team, i) => ({
    position: i + 1,
    team,
    played: 0,
    won: 0,
    drawn: 0,
    lost: 0,
    points: 0,
    gamePoints: 0,
    totalPoints: 0,
    lastUpdated: new Date().toISOString()
  }));
});

// Check if a WHS ID already exists in the database
export const isWhsIdInUse = (whsId: string): boolean => {
  for (const team in playersDataByTeam) {
    const playerExists = playersDataByTeam[team].some(player => player.whsId === whsId);
    if (playerExists) {
      return true;
    }
  }
  return false;
};

// Helper functions
export const addPlayerToTeam = (
  teamName: string, 
  player: { 
    id: number, 
    name: string, 
    whsId: string,
    nominated: boolean,
    matchesPlayed?: number,
    matchesWon?: number,
    matchesDrawn?: number,
    matchesLost?: number,
    originalTeam?: string,
    originalDivision?: string
  }
) => {
  if (isWhsIdInUse(player.whsId) && !player.originalTeam) {
    // If this is a new player with a duplicated WHS ID, return null
    return null;
  }
  
  const newPlayer = {
    id: player.id,
    name: player.name,
    whsId: player.whsId,
    nominated: player.nominated,
    matchesPlayed: player.matchesPlayed || 0,
    matchesWon: player.matchesWon || 0,
    matchesDrawn: player.matchesDrawn || 0,
    matchesLost: player.matchesLost || 0,
    originalTeam: player.originalTeam,
    originalDivision: player.originalDivision
  };
  
  if (teamName in playersDataByTeam) {
    // Check if the player already exists in this team
    const existingPlayerIndex = playersDataByTeam[teamName].findIndex(p => p.id === player.id);
    if (existingPlayerIndex === -1) {
      // Only add if the player isn't already in the team
      playersDataByTeam[teamName].push(newPlayer);
    }
  } else {
    playersDataByTeam[teamName] = [newPlayer];
  }
  
  return newPlayer;
};

export const updatePlayerNominations = (teamName: string, players: Array<{
  id: number;
  name: string;
  whsId: string;
  nominated: boolean;
  matchesPlayed: number;
  matchesWon: number;
  matchesDrawn: number;
  matchesLost: number;
}>) => {
  if (teamName in playersDataByTeam) {
    playersDataByTeam[teamName] = [...players];
    return true;
  }
  return false;
};

export const getPlayerRankingsForDivision = (division: string) => {
  const teamsInDivision = teamsByDivision[division as keyof typeof teamsByDivision] || [];
  
  const allPlayers = teamsInDivision.flatMap(team => {
    const teamPlayers = playersDataByTeam[team] || [];
    return teamPlayers.map(player => ({
      ...player,
      team
    }));
  });
  
  const playersWithPoints = allPlayers.map(player => ({
    ...player,
    points: player.matchesWon + (player.matchesDrawn * 0.5)
  }));
  
  return playersWithPoints.sort((a, b) => b.points - a.points);
};

export const updateLeagueStandings = (
  division: string,
  homeTeam: string,
  awayTeam: string,
  homeScore: number,
  awayScore: number,
  wasAlreadyCompleted: boolean = false
) => {
  if (!leagueStandings[division]) {
    console.error(`Division ${division} not found in standings`);
    return;
  }

  const homeTeamIndex = leagueStandings[division].findIndex(team => team.team === homeTeam);
  const awayTeamIndex = leagueStandings[division].findIndex(team => team.team === awayTeam);

  if (homeTeamIndex === -1 || awayTeamIndex === -1) {
    console.error(`One or both teams not found in standings: ${homeTeam}, ${awayTeam}`);
    return;
  }

  console.log(`Updating league standings for: `, {
    division,
    homeTeam,
    awayTeam,
    homeScore,
    awayScore,
    wasAlreadyCompleted
  });

  if (wasAlreadyCompleted) {
    console.log('Match was already completed, skipping standings update');
    return;
  }

  const currentTime = new Date().toISOString();
  leagueStandings[division].forEach(team => {
    team.lastUpdated = currentTime;
  });

  leagueStandings[division][homeTeamIndex].played += 1;
  leagueStandings[division][awayTeamIndex].played += 1;
  
  leagueStandings[division][homeTeamIndex].gamePoints += homeScore;
  leagueStandings[division][awayTeamIndex].gamePoints += awayScore;

  if (homeScore > awayScore) {
    leagueStandings[division][homeTeamIndex].won += 1;
    leagueStandings[division][homeTeamIndex].points += 2;
    leagueStandings[division][awayTeamIndex].lost += 1;
  } else if (awayScore > homeScore) {
    leagueStandings[division][awayTeamIndex].won += 1;
    leagueStandings[division][awayTeamIndex].points += 2;
    leagueStandings[division][homeTeamIndex].lost += 1;
  } else {
    leagueStandings[division][homeTeamIndex].drawn += 1;
    leagueStandings[division][homeTeamIndex].points += 1;
    leagueStandings[division][awayTeamIndex].drawn += 1;
    leagueStandings[division][awayTeamIndex].points += 1;
  }

  leagueStandings[division][homeTeamIndex].totalPoints = 
    leagueStandings[division][homeTeamIndex].points + 
    leagueStandings[division][homeTeamIndex].gamePoints;
  
  leagueStandings[division][awayTeamIndex].totalPoints = 
    leagueStandings[division][awayTeamIndex].points + 
    leagueStandings[division][awayTeamIndex].gamePoints;

  leagueStandings[division].sort((a, b) => {
    return b.totalPoints - a.totalPoints;
  });

  leagueStandings[division].forEach((team, index) => {
    team.position = index + 1;
  });

  console.log(`League standings for ${division} updated after match: ${homeTeam} vs ${awayTeam}`);
  console.log('Updated standings:', leagueStandings[division]);
};
